import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { type ScannedLine, ScanInput, addScan } from '../../components/scan-input';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { type Columns, DataTable } from '../../components/ui/data-table';
import { Dialog } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { api, errorMessage } from '../../lib/api';
import type { InboundDetail, InboundRow } from '../../lib/types';
import { formatDateTime } from '../../lib/utils';

export function InboundPage() {
  const { t, i18n } = useTranslation();
  const [openId, setOpenId] = useState<string>();
  const [showAll, setShowAll] = useState(false);
  const list = useQuery({
    queryKey: ['warehouse', 'inbound', showAll],
    queryFn: () =>
      api<{ items: InboundRow[] }>(`/warehouse/inbound${showAll ? '' : '?status=expected'}`),
  });

  const columns = useMemo<Columns<InboundRow>>(
    () => [
      {
        id: 'po',
        header: t('wh.purchaseOrder'),
        accessorFn: (r) => r.purchaseOrderNumber ?? '—',
        cell: ({ row }) => (
          <button
            type="button"
            className="font-medium hover:underline"
            onClick={() => {
              setOpenId(row.original.id);
            }}
          >
            {row.original.purchaseOrderNumber ?? '—'}
          </button>
        ),
      },
      { id: 'supplier', header: t('po.supplier'), accessorFn: (r) => r.supplierName ?? '—' },
      { id: 'location', header: t('wh.location'), accessorFn: (r) => r.locationCode },
      { id: 'lines', header: t('wh.lines'), accessorFn: (r) => r.lines },
      {
        id: 'status',
        header: t('po.status'),
        accessorFn: (r) => r.status,
        cell: ({ row }) => (
          <Badge variant={row.original.status === 'received' ? 'success' : 'secondary'}>
            {t(`wh.inboundStatuses.${row.original.status}`)}
          </Badge>
        ),
      },
      {
        id: 'expected',
        header: t('wh.expectedAt'),
        accessorFn: (r) => r.expectedAt ?? '',
        cell: ({ row }) =>
          row.original.expectedAt ? formatDateTime(row.original.expectedAt, i18n.language) : '—',
      },
    ],
    [t, i18n.language],
  );

  return (
    <>
      <PageHeader
        title={t('wh.inboundTitle')}
        description={t('wh.inboundHint')}
        actions={
          <Button
            variant="outline"
            onClick={() => {
              setShowAll((v) => !v);
            }}
          >
            {showAll ? t('wh.onlyExpected') : t('wh.showAll')}
          </Button>
        }
      />
      {list.error ? (
        <Alert variant="destructive" className="mb-4">
          {errorMessage(list.error)}
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isPending}
        rowKey={(r) => r.id}
      />
      {openId ? (
        <ReceiveDialog
          id={openId}
          onClose={() => {
            setOpenId(undefined);
          }}
        />
      ) : null}
    </>
  );
}

function ReceiveDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inbound = useQuery({
    queryKey: ['warehouse', 'inbound', 'detail', id],
    queryFn: () => api<InboundDetail>(`/warehouse/inbound/${id}`),
  });
  const [scanned, setScanned] = useState<ScannedLine[]>([]);
  const [damaged, setDamaged] = useState<Record<string, number>>({});
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<string>();
  const [busy, setBusy] = useState(false);
  const items = inbound.data?.items ?? [];
  const open = inbound.data?.status === 'expected' || inbound.data?.status === 'in_transit';

  const countFor = (sku: string, gtin: string | null) =>
    scanned.filter((s) => s.code === sku || s.code === gtin).reduce((n, s) => n + s.qty, 0);

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const lines = items
        .map((i) => ({
          code: i.sku,
          qty: countFor(i.sku, i.gtin),
          damaged: damaged[i.sku] ?? 0,
        }))
        .filter((l) => l.qty + l.damaged > 0);
      const unknown = scanned.filter(
        (s) => !items.some((i) => i.sku === s.code || i.gtin === s.code),
      );
      const res = await api<{ discrepancies: unknown[]; purchaseOrderStatus: string | null }>(
        `/warehouse/inbound/${id}/receive`,
        { method: 'POST', body: { lines: [...lines, ...unknown] } },
      );
      await queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      setResult(
        res.discrepancies.length
          ? t('wh.receivedWithDiscrepancies', { count: res.discrepancies.length })
          : t('wh.receivedOk'),
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('wh.receiveTitle')}
      className="max-w-2xl"
      footer={
        open && !result ? (
          <Button disabled={busy || scanned.length === 0} onClick={() => void submit()}>
            {t('wh.completeReceipt')}
          </Button>
        ) : null
      }
    >
      <div className="grid gap-4 text-sm">
        {open && !result ? (
          <ScanInput
            onScan={(code) => {
              setScanned((lines) => addScan(lines, code));
            }}
          />
        ) : null}
        <table className="w-full">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">{t('wh.sku')}</th>
              <th>{t('wh.item')}</th>
              <th className="text-right">{t('wh.expected')}</th>
              <th className="text-right">{t('wh.scanned')}</th>
              <th className="text-right">{t('wh.damaged')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const got = open ? countFor(i.sku, i.gtin) : i.qtyReceived;
              const bad = open ? (damaged[i.sku] ?? 0) : i.qtyDamaged;
              return (
                <tr key={i.id} className="border-t">
                  <td className="py-1 font-mono">{i.sku}</td>
                  <td>{i.name}</td>
                  <td className="text-right">{i.qtyExpected}</td>
                  <td
                    className={
                      got + bad === i.qtyExpected ? 'text-right' : 'text-right text-amber-700'
                    }
                  >
                    {got}
                  </td>
                  <td className="w-20 text-right">
                    {open && !result ? (
                      <Input
                        type="number"
                        min={0}
                        aria-label={`${t('wh.damaged')} ${i.sku}`}
                        value={damaged[i.sku] ?? 0}
                        onChange={(e) => {
                          setDamaged((d) => ({
                            ...d,
                            [i.sku]: Math.max(0, Number(e.target.value)),
                          }));
                        }}
                        className="h-8 text-right"
                      />
                    ) : (
                      bad
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {scanned
          .filter((s) => !items.some((i) => i.sku === s.code || i.gtin === s.code))
          .map((s) => (
            <Alert key={s.code} variant="warning">
              {t('wh.notInDelivery', { code: s.code })}
            </Alert>
          ))}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        {result ? <Alert variant="success">{result}</Alert> : null}
      </div>
    </Dialog>
  );
}
